import { useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import Compressor from 'compressorjs';
import type { IInfluencer } from '../../../redux/buzzbridge/types';
import type { IProject } from '../../../types/global/helper';
import type { ThemeStyle } from '../../../module/ThemeManager';
import { Colors } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import MediaAdderComponent from '../../MediaAdderComponent/MediaAdderComponent';
import { PhotoGrid } from '../../WorkProfile/PhotoGrid';

export type GalleryUploadItem = { file: File; previewUrl: string };

function normalizeProjectsFromInfluencer(influencer: IInfluencer): IProject[] {
    const raw = influencer.projects as Array<string | IProject> | undefined;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
        .map(item => {
            if (typeof item === 'string') return { _id: item, title: 'Project' } as IProject;
            if (item && typeof item === 'object' && '_id' in item) {
                return {
                    _id: item._id,
                    title: (item as IProject).title || 'Project',
                    feature_image: (item as IProject).feature_image,
                    project_gallery: (item as IProject).project_gallery,
                } as IProject;
            }
            return null;
        })
        .filter((p): p is IProject => Boolean(p?._id));
}

export interface EditProfileMediaExtrasProps {
    projects: IProject[];
    onProjectsChange: (projects: IProject[]) => void;
    galleryUrls: string[];
    onGalleryUrlsChange: (urls: string[]) => void;
    pendingGalleryUploads: GalleryUploadItem[];
    onPendingGalleryUploadsChange: (items: GalleryUploadItem[]) => void;
    themeTokens: Colors;
    themeStyle: ThemeStyle;
}

export function initialMediaExtrasState(influencer: IInfluencer) {
    return {
        projects: normalizeProjectsFromInfluencer(influencer),
        galleryUrls: [...(influencer.gallery || [])],
        pendingGalleryUploads: [] as GalleryUploadItem[],
    };
}

export function EditProfileMediaExtras({
    projects,
    onProjectsChange,
    galleryUrls,
    onGalleryUrlsChange,
    pendingGalleryUploads,
    onPendingGalleryUploadsChange,
    themeTokens,
    themeStyle,
}: EditProfileMediaExtrasProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [compressing, setCompressing] = useState(false);

    const compressImageFile = (file: File): Promise<File> =>
        new Promise((resolve, reject) => {
            // eslint-disable-next-line no-new
            new Compressor(file, {
                quality: 0.85,
                maxWidth: 1920,
                maxHeight: 1920,
                success: result => {
                    const blob = result as Blob;
                    resolve(new File([blob], file.name, { type: blob.type || file.type }));
                },
                error: reject,
            });
        });

    const handleGalleryFiles = async (files: FileList | null) => {
        if (!files?.length) return;
        setCompressing(true);
        try {
            const nextPending = [...pendingGalleryUploads];
            const nextUrls = [...galleryUrls];
            for (const file of Array.from(files)) {
                const compressed = await compressImageFile(file);
                const previewUrl = URL.createObjectURL(compressed);
                nextPending.push({ file: compressed, previewUrl });
                nextUrls.push(previewUrl);
            }
            onPendingGalleryUploadsChange(nextPending);
            onGalleryUrlsChange(nextUrls);
        } finally {
            setCompressing(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveGalleryAt = (index: number) => {
        const url = galleryUrls[index];
        const pendingIdx = pendingGalleryUploads.findIndex(p => p.previewUrl === url);
        if (pendingIdx >= 0) {
            URL.revokeObjectURL(pendingGalleryUploads[pendingIdx].previewUrl);
            onPendingGalleryUploadsChange(pendingGalleryUploads.filter((_, i) => i !== pendingIdx));
        }
        onGalleryUrlsChange(galleryUrls.filter((_, i) => i !== index));
    };

    const displayGallery = galleryUrls;

    return (
        <>
            <Box>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: themeTokens.form_label,
                        mb: 1,
                    }}>
                    Portfolio projects
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mb: 2,
                        color: themeTokens.placeholderGray,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    Add published work from your library. These appear on your public creator profile.
                </Typography>
                <MediaAdderComponent
                    mediaType="projects"
                    mediaValues={projects}
                    maxSelection={12}
                    visualVariant="brand"
                    title="Projects"
                    onSubmit={(added: IProject[]) => {
                        const merged = [...projects];
                        added.forEach(p => {
                            if (!merged.some(m => m._id === p._id)) merged.push(p);
                        });
                        onProjectsChange(merged);
                    }}
                    onDelete={(project: IProject) => {
                        onProjectsChange(projects.filter(p => p._id !== project._id));
                    }}
                />
            </Box>

            <Box>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: themeTokens.form_label,
                        mb: 1,
                    }}>
                    Gallery
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mb: 2,
                        color: themeTokens.placeholderGray,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    Showcase photos on your public profile (separate from portfolio project cards).
                </Typography>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={e => void handleGalleryFiles(e.target.files)}
                />

                {displayGallery.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <PhotoGrid
                            photos={displayGallery}
                            columns={3}
                            rowHeight={120}
                            isEditMode
                            onEditClick={handleRemoveGalleryAt}
                        />
                    </Box>
                )}

                <Button
                    variant="outlined"
                    disabled={compressing}
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<AddPhotoAlternateIcon />}
                    sx={{
                        borderColor: themeTokens.highlight,
                        color: themeTokens.highlight,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: themeTokens.highlight,
                            backgroundColor: themeTokens.form_divider,
                        },
                    }}>
                    {compressing ? 'Processing…' : 'Add gallery photos'}
                </Button>
            </Box>
        </>
    );
}
